import React from "react";
import { Component } from "react";
import { Action } from "typescript-fsa";
import { IntlShape } from "react-intl";

import { Api } from "@api/index";
import { ApiRequest } from "@api/requests";
import { createApiComponent, ApiComponentProps } from "@api/components/ApiComponent";
import { Localisation } from "@localisation/index";
import { Locale } from "@localisation/models";
import { EntityForm } from "@orm/components/EntityForm";
import { messages as uiMessages } from "@ui/messages";
import { ValidateField } from "@validation/models";
import { messages } from "../../messages";
import {
    User,
    IUser,
    Organisation,
    Role,
    PermissionLevel,
} from "../../models";

export interface UserFormOwnProps {
    mode?: "create" | "edit";
    onSuccess?: () => void;
    user?: User;
    intl: IntlShape;
}

export interface UserFormDispatchProps {
    usersStore: (user: IUser) => Action<ApiRequest>;
    usersUpdate: (user: IUser) => Action<ApiRequest>;
}

export interface UserFormStateProps {
    numUsers: number;
    organisations: Organisation[];
    roles: Role[];
    currentUserOrganisation: number;
    createUserLevel: PermissionLevel;
    updateUserLevel: PermissionLevel;
    organisationsLastLoaded: Date;
    rolesLastLoaded: Date;
    locale: Locale;
}

type Props = UserFormOwnProps & UserFormStateProps & UserFormDispatchProps;

let ApiComponent: React.ComponentClass<ApiComponentProps> = null;

export class UserForm extends Component<Props, {}> {
    private formProps: any = {
        className: "user-form",
        form: "user-form",
        submitText: null,
        dispatcher: null,
        fields: [],
        intl: null,
        initialValues: {},
        locale: null,
    };

    public componentWillMount() {
        const {
            mode,
            user,
            organisations,
            roles,
            organisationsLastLoaded,
            rolesLastLoaded,
            createUserLevel,
            updateUserLevel,
            currentUserOrganisation,
            usersStore,
            usersUpdate,
            locale,
            intl: { formatMessage },
        } = this.props;

        // Initialise the requests.
        const actionCreators = [Api.rolesIndex];
        const lastLoaded = [rolesLastLoaded];

        // Set the dispatcher, submit text, password rules, requests and
        // initial values based on the mode.
        this.formProps.dispatcher = usersStore;
        this.formProps.submitText = formatMessage(uiMessages.buttonCreate);
        this.formProps.intl = this.props.intl;
        this.formProps.locale = locale;
        let passwordRules: ValidateField[] = [
            { type: "required" },
            { type: "minLength", length: 8 },
            { type: "maxLength", length: 50 },
        ];
        this.formProps.initialValues = {};
        let organisationDisabled = createUserLevel !== "system";
        if (mode != null && mode === "edit") {
            this.formProps.dispatcher = usersUpdate;
            this.formProps.submitText = formatMessage(uiMessages.buttonUpdate);
            passwordRules = [
                { type: "minLength", length: 8 },
                { type: "maxLength", length: 50 },
            ];
            if (user != null) {
                this.formProps.initialValues = user.toJS() as IUser;
            }
            organisationDisabled = updateUserLevel !== "system";

            // Set the requests based on the edit user level.
            if (updateUserLevel === "system") {
                actionCreators.push(Api.organisationsIndex);
                lastLoaded.push(organisationsLastLoaded);
            }
        }
        else {
            // Set the requests based on the create user level.
            if (createUserLevel === "system") {
                actionCreators.push(Api.organisationsIndex);
                lastLoaded.push(organisationsLastLoaded);
            }
            else {
                // Set the initial organisation for users without system-level
                // create user permissions.
                this.formProps.initialValues.organisation = currentUserOrganisation;
            }
        }

        this.formProps.fields = [
            {
                name: "name",
                label: formatMessage(messages.userColumnName),
                rules: [
                    { type: "required" },
                    { type: "minLength", length: 3 },
                    { type: "maxLength", length: 255 },
                ],
            },
            {
                name: "email",
                label: formatMessage(messages.userColumnEmail),
                type: "email",
                rules: [
                    { type: "required" },
                    { type: "email" },
                ],
            },
            {
                name: "password",
                label: formatMessage(messages.userColumnPassword),
                type: "password",
                rules: passwordRules,
            },
            {
                name: "organisation",
                label: formatMessage(messages.userColumnOrganisation),
                type: "select",
                options: organisations,
                disabled: organisationDisabled,
                rules: [
                    {
                        type: "oneOf",
                        properties: organisations.map(
                            (org: Organisation) => ({ value: org.id, label: org.name }),
                        ),
                    },
                ],
            },
            {
                name: "roles",
                label: formatMessage(messages.userColumnRoles),
                type: "select",
                multiple: true,
                options: roles,
                valueKey: "name",
                labelKey: "label",
                rules: [
                    {
                        type: "oneOf",
                        properties: roles.map(
                            (role: Role) => ({
                                value: role.name,
                                label: Localisation.translateMap(role.label, locale),
                            }),
                        ),
                    },
                ],
            },
        ];

        ApiComponent = createApiComponent({
            showRefresh: false,
            actionCreators,
            lastLoaded,
        });
    }

    public componentWillReceiveProps(nextProps: Props) {
        const { mode } = this.props;

        // If we're in create mode and the number of users increases.
        if (nextProps.onSuccess != null &&
            (mode == null || mode === "create") &&
            this.props.numUsers + 1 === nextProps.numUsers
        ) {
            // There's a new user, so we can fire the onSuccess function.
            nextProps.onSuccess();
        }
        // Else if we're in edit mode and the updatedAt time on this user changes.
        else if (nextProps.onSuccess != null &&
            mode != null && mode === "edit" &&
            this.props.user != null &&
            this.props.user.updatedAt.getTime() !== nextProps.user.updatedAt.getTime()
        ) {
            // The user was , so we can fire the onSuccess function.
            nextProps.onSuccess();
        }
    }

    public render(): JSX.Element {
        // Type the form component
        // type TypedForm = new () => EntityForm<IUser, AuthState>;
        // // type TypedForm = EntityForm<IUser, AuthState>;
        // // interface TypedForm { new (): EntityForm<IUser, AuthState>; };
        // const TypedForm = EntityForm as TypedForm;

        return (
            <ApiComponent>
                <EntityForm {...this.formProps} />
            </ApiComponent>
        );
    }
}
